<template>
  <div
    :class="containerKls"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- prepend slot -->
    <div v-if="$slots.prepend" :class="nsInput.be('group', 'prepend')">
      <slot name="prepend" />
    </div>
    <el-tooltip
      ref="popperRef"
      :disabled="suggestionDisabled"
      :visible="customPopperVisible || suggestionVisible"
      :placement="placement"
      :fallback-placements="['bottom-start', 'top-start']"
      :popper-class="[nsInput.e('popper'), popperClass]"
      :teleported="teleported"
      :gpu-acceleration="false"
      pure
      manual-mode
      effect="light"
      trigger="click"
      :transition="`${nsInput.namespace.value}-zoom-in-top`"
      persistent
      @before-show="onSuggestionShow"
      @hide="onSuggestionHide"
    >
      <div ref="wrapperRef" :class="wrapperKls" :style="wrapperStyle">
        <!-- prefix slot -->
        <span v-if="prefixVisible" :class="nsInput.e('prefix')">
          <span :class="nsInput.e('prefix-inner')">
            <slot name="prefix" />
            <el-icon v-if="prefixIcon" :class="nsInput.e('icon')">
              <component :is="prefixIcon" />
            </el-icon>
          </span>
        </span>
        <!-- tags / input -->
        <div :class="[nsInput.e('content')]">
          <transition v-if="inputedText.length">
            <div :class="[nsInput.e('tags')]">
              <template v-if="collapseTags">
                <el-tag
                  v-for="(item, index) in showTagList"
                  :key="`tag-${item}-${index}`"
                  :class="[nsInput.e('tag')]"
                  :closable="!inputDisabled && !readonly"
                  :size="collapseTagSize"
                  :hit="tagHit"
                  :type="tagType"
                  :title="item"
                  disable-transitions
                  @close="handleDeleteTag($event, item, index)"
                >
                  <span :class="[nsInput.e('tag-text')]">{{ item }}</span>
                </el-tag>
                <el-tag
                  v-if="inputedText.length > maxCollapseTags"
                  :class="[nsInput.e('tag')]"
                  :closable="false"
                  :size="collapseTagSize"
                  :hit="tagHit"
                  :type="tagType"
                  disable-transitions
                >
                  <el-tooltip
                    ref="tagTooltipRef"
                    :disabled="!collapseTagsTooltip"
                    :fallback-placements="['bottom', 'top', 'right', 'left']"
                    :effect="effect"
                    placement="bottom"
                    :teleported="teleported"
                  >
                    <template #default>
                      <span :class="[nsInput.e('tag-text')]"
                        >+ {{ inputedText.length - maxCollapseTags }}</span
                      >
                    </template>
                    <template #content>
                      <div :class="nsInput.e('collapse-tags')">
                        <el-tag
                          v-for="(cItem, cIndex) in collapseTagList"
                          :key="`cTag-${cItem}-${cIndex}`"
                          :class="nsInput.e('collapse-tag')"
                          :closable="!inputDisabled && !readonly"
                          :size="collapseTagSize"
                          :hit="tagHit"
                          :type="tagType"
                          :title="cItem"
                          disable-transitions
                          :style="{ margin: '2px' }"
                          @close="handleDeleteTooltipTag($event, cItem)"
                        >
                          <span :class="[nsInput.e('tag-text')]">{{
                            cItem
                          }}</span>
                        </el-tag>
                      </div>
                    </template>
                  </el-tooltip>
                </el-tag>
              </template>
              <template v-else>
                <el-tag
                  v-for="(item, index) in inputedText"
                  :key="`tag-${item}-${index}`"
                  :class="[nsInput.e('tag')]"
                  :closable="!inputDisabled && !readonly"
                  :size="collapseTagSize"
                  :hit="tagHit"
                  :type="tagType"
                  :title="item"
                  disable-transitions
                  @close="handleDeleteTag($event, item, index)"
                >
                  <span :class="[nsInput.e('tag-text')]">{{ item }}</span>
                </el-tag>
              </template>
            </div>
          </transition>
          <input
            v-show="!hiddenOnDisabled || (!inputDisabled && !readonly)"
            :id="inputId"
            ref="input"
            type="text"
            :class="nsInput.e('inner')"
            :disabled="inputDisabled"
            :formatter="formatter"
            :parser="parser"
            :readonly="readonly"
            :autocomplete="autocomplete"
            :autofocus="autofocus"
            :tabindex="tabindex"
            :aria-label="label"
            :placeholder="placeholder"
            :form="props.form"
            @compositionstart="handleCompositionStart"
            @compositionupdate="handleCompositionUpdate"
            @compositionend="handleCompositionEnd"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"
            @keydown="handleKeydown"
            @keydown.esc="handleKeydownEscape"
            @keydown.enter.stop.prevent="handleKeydownEnter"
            @keydown.delete="handleKeydownDelete"
            @keydown.up.prevent="highlight(highlightedIndex - 1)"
            @keydown.down.prevent="highlight(highlightedIndex + 1)"
            @keydown.tab="hideSuggestion"
          />
        </div>
        <!-- suffix slot -->
        <span v-if="suffixVisible" :class="nsInput.e('suffix')">
          <span :class="nsInput.e('suffix-inner')">
            <template v-if="!showClear || !limitVisible">
              <slot name="suffix" />
              <el-icon v-if="suffixIcon" :class="nsInput.e('icon')">
                <component :is="suffixIcon" />
              </el-icon>
            </template>
            <el-icon
              v-if="showClear"
              :class="[nsInput.e('icon'), nsInput.e('clear')]"
              @mousedown.prevent="NOOP"
              @click="clear"
            >
              <circle-close />
            </el-icon>
            <span v-if="limitVisible" :class="nsInput.e('count')">
              <span :class="nsInput.e('count-inner')">
                {{ inputedText.length }} / {{ limit }}
              </span>
            </span>
            <el-icon
              v-if="validateState && validateIcon && needStatusIcon"
              :class="[
                nsInput.e('icon'),
                nsInput.e('validateIcon'),
                nsInput.is('loading', validateState === 'validating'),
              ]"
            >
              <component :is="validateIcon" />
            </el-icon>
          </span>
        </span>
      </div>
      <!--suggestions content-->
      <template #content>
        <div
          ref="regionRef"
          :class="[
            nsInput.b('suggestion'),
            nsInput.is('loading', suggestionLoading),
          ]"
          :style="{
            [fitInputWidth ? 'width' : 'minWidth']: dropdownWidth,
            outline: 'none',
          }"
          role="region"
        >
          <slot name="popper-content">
            <el-scrollbar
              :id="listboxId"
              tag="ul"
              :wrap-class="nsInput.be('suggestion', 'wrap')"
              :view-class="nsInput.be('suggestion', 'list')"
              role="listbox"
            >
              <li
                v-if="suggestionLoading"
                key="li-loading"
                :class="nsInput.bem('suggestion', 'list-item', 'loading')"
              >
                <el-icon :class="nsInput.is('loading')">
                  <Loading />
                </el-icon>
              </li>
              <template v-else-if="dropdownSuggestions.length">
                <li
                  v-for="(item, index) in dropdownSuggestions"
                  :id="`${listboxId}-item-${index}`"
                  :key="index"
                  :class="{
                    highlighted: highlightedIndex === index,
                    'is-disabled':
                      suggestionItemDisabled && suggestionItemDisabled(item),
                  }"
                  role="option"
                  :aria-selected="highlightedIndex === index"
                  @click="handleSuggestionSelect(item)"
                >
                  <slot name="suggestion-item" :item="item">{{
                    item[valueKey]
                  }}</slot>
                </li>
              </template>
              <li
                v-else
                key="li-empty"
                :class="nsInput.bem('suggestion', 'list-item', 'empty')"
              >
                <slot name="suggestion-empty">
                  <span>{{ noMatchText }}</span>
                </slot>
              </li>
            </el-scrollbar>
          </slot>
        </div>
      </template>
    </el-tooltip>
    <!-- append slot -->
    <div v-if="$slots.append" :class="nsInput.be('group', 'append')">
      <slot name="append" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUpdated,
  reactive,
  ref,
  shallowRef,
  useAttrs as useRawAttrs,
  useSlots,
  watch,
} from 'vue'
import { debounce, isEqual, isNil } from 'lodash-unified'
import { onClickOutside } from '@vueuse/core'
import {
  useFormDisabled,
  useFormItem,
  useFormItemInputId,
  useFormSize,
} from '@element-plus/components/form'
import {
  NOOP,
  ValidateComponentsMap,
  debugWarn,
  generateId,
  getStyle,
  isKorean,
  isString,
  isUndefined,
} from '@element-plus/utils'
import {
  useCursor,
  useFocusController,
  useLocale,
  useNamespace,
} from '@element-plus/hooks'
import {
  CHANGE_EVENT,
  EVENT_CODE,
  UPDATE_MODEL_EVENT,
} from '@element-plus/constants'
import ElScrollbar from '@element-plus/components/scrollbar'
import ElTooltip from '@element-plus/components/tooltip'
import ElTag from '@element-plus/components/tag'
import { ElIcon } from '@element-plus/components/icon'
import { CircleClose, Loading } from '@element-plus/icons-vue'
import { multipleInputEmits, multipleInputProps } from './multiple-input'
import type {
  MultipleInputSuggestionData,
  SuggestionData,
} from './multiple-input'
import type { TooltipInstance } from '@element-plus/components/tooltip'

defineOptions({
  name: 'ElMultipleInput',
  inheritAttrs: false,
})
const props = defineProps(multipleInputProps)
const emit = defineEmits(multipleInputEmits)
const { t } = useLocale()

const rawAttrs = useRawAttrs()
const slots = useSlots()

const nsInput = useNamespace('multiple-input')

const inputSize = useFormSize()
const inputDisabled = useFormDisabled()

const { form, formItem } = useFormItem()
const { inputId } = useFormItemInputId(props, {
  formItemContext: formItem,
})

const listboxId = computed(() => nsInput.b(String(generateId())))

// ref
const input = shallowRef<HTMLInputElement>()
const tagTooltipRef = ref<TooltipInstance>()
const popperRef = ref<TooltipInstance>()
const regionRef = ref<HTMLElement>()

// wrapper box sizing
const mounted = ref(false)
const boxSizing = reactive({
  defaultPadding: 0,
  suffix: {
    width: 0,
    margin: 0,
    maxWidth: 120,
    minWidth: 14, // 14
    minWidth2: 56, // 14 + 8 + 34
  },
  prefix: {
    width: 0,
    margin: 0,
    maxWidth: 120,
    minWidth: 14,
  },
})

// inner input
const hovering = ref(false)
const isComposing = ref(false)
const inputText = ref('') // inner input text
const noMatchText = computed(() => props.noMatchText || t('el.select.noMatch'))

// suggestions
let ignoreFocusEvent = false
let ignoreOutsideEvent = false
const dropdownSuggestions = ref<SuggestionData>([])
const dropdownActivated = ref(false)
const dropdownDisabled = ref(false)
const dropdownWidth = ref('')
const highlightedIndex = ref(-1)
const dropdownLoading = ref(false)

// custom suggestion popper
const customPopper = ref(!!slots['popper-content'])
const customPopperActivated = ref(false)

// suggestions computed
const suggestionDisabled = computed(
  () =>
    inputDisabled.value ||
    props.readonly ||
    (!customPopper.value &&
      !props.suggestions.length &&
      !props.fetchSuggestions)
)

const suggestionVisible = computed(
  () =>
    ((inputText.value && props.displayWithoutMatch) ||
      dropdownSuggestions.value.length > 0) &&
    dropdownActivated.value
)

const customPopperVisible = computed(
  () => customPopper.value && customPopperActivated.value
)

const suggestionLoading = computed(
  () => !props.hideLoading && dropdownLoading.value
)

const normalizeSuggestions = computed<SuggestionData>(() =>
  normalize(props.suggestions)
)

// render computed
const _ref = computed(() => input.value)

const collapseTagSize = computed(() =>
  ['small', 'large'].includes(inputSize.value) ? inputSize.value : 'default'
)

const containerKls = computed(() => [
  nsInput.b(),
  nsInput.m(inputSize.value),
  nsInput.is('disabled', inputDisabled.value),
  nsInput.is('exceed', inputExceed.value),
  nsInput.is('stretch', props.stretch),
  nsInput.is('reserve', props.reserveStyle),
  {
    [nsInput.b('group')]: slots.prepend || slots.append,
    [nsInput.bm('group', 'append')]: slots.append,
    [nsInput.bm('group', 'prepend')]: slots.prepend,
    [nsInput.m('prefix')]: slots.prefix || props.prefixIcon,
    [nsInput.m('suffix')]: slots.suffix || props.suffixIcon || props.clearable,
  },
  rawAttrs.class,
])

const wrapperKls = computed(() => [
  nsInput.e('wrapper'),
  nsInput.is('focus', isFocused.value),
])

const wrapperStyle = computed(() => {
  if (!mounted.value) return {}
  const prefixBox = prefixVisible.value
    ? Math.min(
        Math.max(boxSizing.prefix.width, boxSizing.prefix.minWidth),
        boxSizing.prefix.maxWidth
      ) + boxSizing.prefix.margin
    : 0
  const suffixBox = suffixVisible.value
    ? Math.min(
        Math.max(
          boxSizing.suffix.width,
          limitVisible.value
            ? boxSizing.suffix.minWidth2
            : boxSizing.suffix.minWidth
        ),
        boxSizing.suffix.maxWidth
      ) + boxSizing.suffix.margin
    : 0
  return {
    paddingLeft: `${
      (prefixVisible.value || !inputedText.value.length
        ? boxSizing.defaultPadding
        : Math.floor(boxSizing.defaultPadding / 2)) + prefixBox
    }px`,
    paddingRight: `${boxSizing.defaultPadding + suffixBox}px`,
  }
})

// inner focus/blur event after
const afterFocus = () => {
  if (!ignoreFocusEvent) {
    if (!customPopper.value) {
      dropdownActivated.value = true
      if (props.triggerOnFocus && !suggestionDisabled.value) {
        debouncedGetSuggestionData(inputText.value)
      }
    }
  } else {
    ignoreFocusEvent = false
  }
}

const afterBlur = () => {
  setTimeout(() => {
    // validate current focus event is inside el-tooltip-content
    // if so, ignore the blur event and the next focus event
    if (popperRef.value?.isFocusInsideContent()) {
      ignoreFocusEvent = true
      return
    }
    // Scene one：
    // if event form delete tag where at tooltip content
    // keep suggestion popper visible
    if (tagTooltipRef.value?.isFocusInsideContent()) {
      ignoreOutsideEvent = true
      return
    }
    ;(dropdownActivated.value || customPopperActivated.value) &&
      hideSuggestion()
  }, 0)
}

const { wrapperRef, isFocused, handleFocus, handleBlur } = useFocusController(
  _ref,
  {
    afterFocus,
    afterBlur() {
      afterBlur()
      if (props.validateEvent) {
        formItem?.validate?.('blur').catch((err) => debugWarn(err))
      }
    },
  }
)

// validate status computed
const needStatusIcon = computed(() => form?.statusIcon ?? false)
const validateState = computed(() => formItem?.validateState || '')
const validateIcon = computed(
  () => validateState.value && ValidateComponentsMap[validateState.value]
)

// modelValue
const normalizeModelValue = computed<string[] | string>(() =>
  isNil(props.modelValue) ? [] : props.modelValue
)

const inputedText = computed<string[]>(() =>
  Array.isArray(normalizeModelValue.value)
    ? normalizeModelValue.value
    : normalizeModelValue.value
    ? normalizeModelValue.value.split(props.separator)
    : []
)

// visible computed
const prefixVisible = computed(() => !!slots.prefix || !!props.prefixIcon)

/* const inputVisible = computed(() => {
  return (
    !mounted.value ||
    (!inputDisabled.value &&
      !props.readonly &&
      (!limitVisible.value || inputedText.value.length < Number(props.limit)))
  )
}) */

const showClear = computed(
  () =>
    props.clearable &&
    !inputDisabled.value &&
    !props.readonly &&
    (hovering.value || isFocused.value) &&
    (((!isString(props.clearable) || props.clearable !== 'Input') &&
      !!inputedText.value.length) ||
      (isString(props.clearable) &&
        props.clearable !== 'Tag' &&
        inputText.value))
)

const limitVisible = computed(
  () =>
    props.showLimit && !!props.limit && !inputDisabled.value && !props.readonly
)

const suffixVisible = computed(
  () =>
    !!slots.suffix ||
    !!props.suffixIcon ||
    showClear.value ||
    limitVisible.value ||
    (!!validateState.value && needStatusIcon.value)
)

const showTagList = computed(() =>
  inputedText.value.slice(0, props.maxCollapseTags)
)

const collapseTagList = computed(() =>
  inputedText.value.slice(props.maxCollapseTags)
)

const inputExceed = computed(
  () =>
    // show exceed style if length of initial value greater then maxlength
    !!limitVisible.value && inputedText.value.length > Number(props.limit)
)

// inner input cursor
const [recordCursor, setCursor] = useCursor(input)

// inner input opearion function
const setNativeInputValue = () => {
  const input = _ref.value // HTML input Element
  const formatterValue = props.formatter
    ? props.formatter(inputText.value)
    : inputText.value
  if (!input || input.value === formatterValue) return
  input.value = formatterValue
}

// inner input event
const handleInput = async (event: Event) => {
  recordCursor()

  let { value } = event.target as HTMLInputElement

  if (props.formatter) {
    value = props.parser ? props.parser(value) : value
  }

  // should not emit input during composition
  // see: https://github.com/ElemeFE/element/issues/10516
  if (isComposing.value) return

  // hack for https://github.com/ElemeFE/element/issues/8548
  // should remove the following line when we don't support IE
  if (value === inputText.value) {
    setNativeInputValue()
    return
  }

  inputText.value = value
  emit('input', value)

  dropdownDisabled.value = false
  if (!props.triggerOnFocus && !value) {
    dropdownDisabled.value = true
    dropdownSuggestions.value = []
    dropdownLoading.value = false
    highlightedIndex.value = -1
  } else if (!suggestionDisabled.value) {
    if (isFocused.value && !dropdownActivated.value) {
      // Scene four：
      // when click suggestion padding that was not belong suggestion item
      // that will close suggestion popper after input has focused
      // should restore dropdownActivated when input
      dropdownActivated.value = true
    }
    debouncedGetSuggestionData(value)
  }

  // ensure native input value is controlled
  // see: https://github.com/ElemeFE/element/issues/12850
  await nextTick()
  setNativeInputValue()
  setCursor()
}

// const handleChange = (event: Event) => {}

// compistion event
const handleCompositionStart = (event: CompositionEvent) => {
  emit('compositionstart', event)
  isComposing.value = true
}

const handleCompositionUpdate = (event: CompositionEvent) => {
  emit('compositionupdate', event)
  const text = (event.target as HTMLInputElement)?.value
  const lastCharacter = text[text.length - 1] || ''
  isComposing.value = !isKorean(lastCharacter)
}

const handleCompositionEnd = (event: CompositionEvent) => {
  emit('compositionend', event)
  if (isComposing.value) {
    isComposing.value = false
    handleInput(event)
  }
}

// tag event
const handleDeleteTag = (
  event: MouseEvent,
  tag: string,
  index?: number
): void => {
  event.stopPropagation() // close tag event stop propagation
  deleteTag(tag, index)
  if (isFocused.value) {
    focus()
  }
}

const handleDeleteTooltipTag = (event: Event, tag: string): void => {
  deleteTag(tag)
  tagTooltipRef.value?.updatePopper?.()
  // scene one:
  // after blur event, the isFocused = false
  // that can't be used as a judgment
  // so the focus manually should be set at clickoutside callback
}

// mouse event
const handleMouseLeave = (evt: MouseEvent) => {
  hovering.value = false
  emit('mouseleave', evt)
}

const handleMouseEnter = (evt: MouseEvent) => {
  hovering.value = true
  emit('mouseenter', evt)
}

// keydown event
const handleKeydown = (evt: KeyboardEvent) => {
  emit('keydown', evt)
}

const handleKeydownEscape = (evt: KeyboardEvent) => {
  if (customPopperVisible.value || suggestionVisible.value) {
    evt.preventDefault()
    evt.stopPropagation()
    hideSuggestion()
  }
}

const handleKeydownEnter = async (evt: KeyboardEvent) => {
  if (
    !suggestionDisabled.value &&
    suggestionVisible.value &&
    highlightedIndex.value >= 0 &&
    highlightedIndex.value < dropdownSuggestions.value.length
  ) {
    handleSuggestionSelect(dropdownSuggestions.value[highlightedIndex.value])
  } else {
    if (inputText.value) {
      enterValue(inputText.value)
      resetInput()
      if (props.triggerOnFocus && !suggestionDisabled.value) {
        debouncedGetSuggestionData(inputText.value) // ''
      }
    }
  }
}

const handleKeydownDelete = (evt: KeyboardEvent) => {
  if (evt.code === EVENT_CODE.delete) return
  const { value } = evt.target as HTMLInputElement
  if (!value) {
    // delete last inputed text
    const nValue = inputedText.value.slice()
    nValue.pop()
    changeValue(nValue)
  }
}

// suggesstions event
const handleSuggestionSelect = (item: any): void => {
  if (suggestionDisabled.value) return
  if (props.suggestionItemDisabled && props.suggestionItemDisabled(item)) return
  enterValue(item[props.valueKey])
  emit('select', item)
  const refresh = !!inputText.value
  resetInput()
  if (props.blurOnSelected) {
    // Scene three:
    // when blur on selected
    // the blur was triggered firstly, and focus event is secondl, finnally the clickoutside was triggered
    // after those event, `dropdownActivated.value` was be false
    // to keep visible when inputing, should set true manually
    dropdownActivated.value = true
    // dropdownDisabled.value = true
    dropdownSuggestions.value = []
    dropdownLoading.value = false
    highlightedIndex.value = -1
  } else if (refresh) {
    if (props.triggerOnFocus && !suggestionDisabled.value) {
      debouncedGetSuggestionData(inputText.value) // ''
    }
  }
}

const onSuggestionShow = async () => {
  await nextTick()
  if (customPopperVisible.value || suggestionVisible.value) {
    dropdownWidth.value = `${wrapperRef.value!.offsetWidth}px`
  }
}

const onSuggestionHide = () => {
  highlightedIndex.value = -1
}

const hideSuggestion = (clear = true) => {
  dropdownActivated.value = false
  customPopperActivated.value = false
  if (clear) {
    dropdownSuggestions.value = []
  }
}

const showPopper = () => {
  if (suggestionDisabled.value) return
  if (customPopper.value) {
    customPopperActivated.value = true
  } else {
    dropdownDisabled.value = false
    dropdownActivated.value = true
    debouncedGetSuggestionData(inputText.value)
  }
}

// focus/blur operation
const focus = async () => {
  // see: https://github.com/ElemeFE/element/issues/18573
  await nextTick()
  _ref.value?.focus()
}

const blur = () => _ref.value?.blur()

// operation function
const enterValue = (text: string): void => {
  const value = inputedText.value.slice()
  if (
    (!props.unique || !value.includes(text)) &&
    (props.limit === 0 || value.length < props.limit)
  ) {
    value.push(text)
    changeValue(value)
  }
}

const changeValue = (value: string[]): void => {
  const newValue = Array.isArray(normalizeModelValue.value)
    ? value
    : value.join(props.separator)
  emit(UPDATE_MODEL_EVENT, newValue)
  if (!isEqual(inputedText.value, newValue)) {
    emit(CHANGE_EVENT, newValue)
  }
}

const deleteTag = (tag: string, index?: number): void => {
  const aIndex = isUndefined(index) ? inputedText.value.indexOf(tag) : index
  if (aIndex > -1 && !inputDisabled.value) {
    const value = inputedText.value.slice()
    value.splice(aIndex, 1)
    changeValue(value)
    emit('remove-tag', tag, aIndex)
  }
}

const resetInput = async () => {
  inputText.value = ''
  await nextTick()
  setNativeInputValue()
  setCursor()
}

const select = () => {
  _ref.value?.select()
}

const clear = () => {
  if (!isString(props.clearable) || props.clearable !== 'Input') {
    changeValue([])
  }
  if (isString(props.clearable) && props.clearable !== 'Tag') {
    resetInput()
  }
  emit('clear')
}

const highlight = (index: number) => {
  if (
    suggestionDisabled.value ||
    !suggestionVisible.value ||
    dropdownLoading.value
  )
    return

  if (index < 0) {
    highlightedIndex.value = -1
    return
  }

  if (index >= dropdownSuggestions.value.length) {
    index = dropdownSuggestions.value.length - 1 // self-aligning
  }
  const suggestion = regionRef.value?.querySelector(
    `.${nsInput.be('suggestion', 'wrap')}`
  )!
  const suggestionList = suggestion?.querySelectorAll<HTMLElement>(
    `.${nsInput.be('suggestion', 'list')} li`
  )!
  if (!suggestionList) return
  const highlightItem = suggestionList[index]
  const scrollTop = suggestion.scrollTop
  const { offsetTop, scrollHeight } = highlightItem

  if (offsetTop + scrollHeight > scrollTop + suggestion.clientHeight) {
    suggestion.scrollTop += scrollHeight
  }
  if (offsetTop < scrollTop) {
    suggestion.scrollTop -= scrollHeight
  }
  highlightedIndex.value = index
  // TODO: use Volar generate dts to fix it.
  ;(input.value as any)!.setAttribute(
    'aria-activedescendant',
    `${listboxId.value}-item-${highlightedIndex.value}`
  )
}

onClickOutside(wrapperRef, () => {
  if (!ignoreFocusEvent) {
    if (tagTooltipRef.value?.isFocusInsideContent()) {
      // Scene one：
      // if event form delete tag where at tooltip content
      // that will trigger blur event and clickoutside event
      // and now focus inside tooltip content, so the `ignoreFocusEvent=false`, `isFocused=false`
      // if want to keep focus inside wrapper and keep suggestion popper visible in the case, should focus manually
      if (ignoreOutsideEvent) {
        focus()
        ignoreOutsideEvent = false
      }
      return
    }
    // Scene five: custom popper, close poper when click popper outside
    if (customPopper.value && popperRef.value?.isFocusInsideContent())
      return // Scene other: hidden suggestion popper
    ;(suggestionVisible.value || customPopperVisible.value) && hideSuggestion()
  } else {
    // Scene two：
    // if event from select suggestion item
    // that will trigger blur event and clickoutside event
    // but, focus inside suggestion popper, the `ignoreFocusEvent=true`
    // if want to keep suggestion popper visible, should use ignoreFocusEvent as the judgement
    // but, that will cause wrapper blur
    // should focus manually
    focus()
  }
})

// render
const getBoxSizing = () => {
  if (wrapperRef.value) {
    boxSizing.defaultPadding = Number.parseInt(
      getStyle(wrapperRef.value, 'paddingLeft'),
      10
    )
    getPrefixBoxSizing()
    getSuffixBoxSizing()
    mounted.value = true
  }
}

const getPrefixBoxSizing = () => {
  if (wrapperRef.value && prefixVisible.value) {
    const prefixDOM = wrapperRef.value.querySelector(`.${nsInput.e('prefix')}`)
    if (prefixDOM) {
      const rect = prefixDOM.getBoundingClientRect()
      boxSizing.prefix.width = Math.floor(rect.width) // have include margin
    }
  }
}

const getSuffixBoxSizing = () => {
  if (wrapperRef.value && suffixVisible.value) {
    const suffixDOM = wrapperRef.value.querySelector(`.${nsInput.e('suffix')}`)
    if (suffixDOM) {
      const rect = suffixDOM.getBoundingClientRect()
      boxSizing.suffix.width = Math.floor(rect.width) // have include margin
    }
  }
}

// update attribute
const setWrapperAttributes = () => {
  // TODO: use Volar generate dts to fix it.
  ;(input.value as any)!.setAttribute('role', 'textbox')
  ;(input.value as any)!.setAttribute('aria-autocomplete', 'list')
  ;(input.value as any)!.setAttribute('aria-controls', 'id')
  ;(input.value as any)!.setAttribute(
    'aria-activedescendant',
    `${listboxId.value}-item-${highlightedIndex.value}`
  )
}

const removeWrapperAttributes = () => {
  // TODO: use Volar generate dts to fix it.
  ;(input.value as any).ref!.removeAttribute('role')
  ;(input.value as any).ref!.removeAttribute('aria-autocomplete')
  ;(input.value as any).ref!.removeAttribute('aria-controls')
  ;(input.value as any).ref!.removeAttribute('aria-activedescendant')
}

// get suggestio data
const normalize = (data: MultipleInputSuggestionData): SuggestionData => {
  if (!Array.isArray(data)) return []
  return data.map((item: any) => {
    if (isString(item)) return { [props.valueKey]: item }
    return item
  })
}

const getSuggestionData = async (query: string) => {
  if (!suggestionDisabled.value && !dropdownDisabled.value) {
    let suggestions: SuggestionData = []
    dropdownLoading.value = true
    if (props.fetchSuggestions) {
      // custom filter
      // Prioritize the use of fetchSuggestion
      // Promise must be resolve
      const result = await props.fetchSuggestions(query)
      suggestions = normalize(result)
      dropdownSuggestions.value = suggestions
    } else if (props.suggestions.length) {
      // inner filter
      suggestions = normalizeSuggestions.value
      dropdownSuggestions.value = suggestions.filter(
        (item: any): boolean =>
          !!item[props.valueKey] &&
          (!query || item[props.valueKey].includes(query))
      )
    }
    highlightedIndex.value =
      props.highlightFirstItem && dropdownSuggestions.value.length ? 0 : -1
    dropdownLoading.value = false
  } else {
    dropdownSuggestions.value = []
    highlightedIndex.value = -1
  }
}
const debouncedGetSuggestionData = debounce(getSuggestionData, props.debounce)

watch(
  () => props.modelValue,
  () => {
    if (props.validateEvent) {
      formItem?.validate?.('change').catch((err) => debugWarn(err))
    }
  }
)

watch(
  () => props.unique,
  (val) => {
    if (val && inputedText.value.length) {
      // auto unique content
      changeValue([...new Set(inputedText.value)])
    }
  }
)

watch(
  () => [props.suggestions, props.fetchSuggestions],
  () => {
    if (dropdownActivated.value && !dropdownDisabled.value) {
      debouncedGetSuggestionData(inputText.value)
    }
  }
)

watch(suggestionDisabled, (val) => {
  if (val) {
    dropdownSuggestions.value = []
    dropdownLoading.value = false
    highlightedIndex.value = -1
    removeWrapperAttributes()
  } else {
    setWrapperAttributes()
  }
})

// native input value is set explicitly
// do not use v-model / :value in template
// see: https://github.com/ElemeFE/element/issues/14521
watch(inputText, () => setNativeInputValue())

onMounted(() => {
  if (!props.formatter && props.parser) {
    debugWarn(
      'ElInput',
      'If you set the parser, you also need to set the formatter.'
    )
  }
  if (!suggestionDisabled.value) {
    setWrapperAttributes()
  }
  getBoxSizing()
  setNativeInputValue()
})

onUpdated(() => {
  if (suffixVisible.value && (!showClear.value || !limitVisible.value)) {
    getSuffixBoxSizing()
  }
  if (prefixVisible.value) {
    getPrefixBoxSizing()
  }

  customPopper.value = !!slots['popper-content']
})

defineExpose({
  /** @description HTML input element */
  input,
  /** @description HTML element: input */
  ref: _ref,
  /** @description suggestions el-tooltip component instance */
  popperRef,
  /** @description HTML input element native method */
  focus,
  /** @description HTML input element native method */
  blur,
  /** @description HTML input element native method */
  select,
  /** @description clear all inputed value or input text */
  clear,
  /** @description remove single inputed value */
  delete: deleteTag,
  /** @description highlight an item in a suggestion */
  highlight,
  /** @description clear input text */
  resetInput,
  /** @description hide suggestion */
  hide: hideSuggestion,
  /** @description show suggestion */
  show: showPopper,
})
</script>
